import { useMemo, useRef } from "react";
import { CategoryTree } from "src/molecules/CategoryTree";
import {
  CategoryTreeWrapper,
  GridWrapper,
  ListingContainer,
} from "./ProductList.styled";
import { AdditionalCategories } from "../AdditionalCategories";
import { CategoryTreeProvider } from "src/contexts/category-tree-context";
import {
  Configure,
  InstantSearch,
  InstantSearchServerState,
  InstantSearchSSRProvider,
} from "react-instantsearch-core-v7";
import { Promotion } from "./Promotion";
import { Grid } from "./Grid";
import { PLPType } from "src/types/PLPType.d";
import { NavigationItems } from "src/types/graphql.d";
import {
  BRAND_DESIGNER_ONLY_FACET,
  BRAND_FACET,
  BRAND_IS_PRIVATE_SALE,
  CATEGORIES_FACET,
  CATEGORY_PAGE_ID_FACET,
  COLOUR_FACET,
  DISCOUNT_FACET,
  GENDERS_FACET,
  IS_DELETED,
  LABELS_TYPE_FACET,
  PATTERN_FACET,
  PRICE_FACET,
  SIZE_FACET,
  STYLE_FACET,
} from "src/utils/algolia";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import contextKeys from "src/utils/algolia/contextKeys";
import { WEB_SHOP } from "src/utils/algolia/analyticsTags";
import { VirtualRefinementList } from "src/atoms/VirtualRefinementList";
import { SearchClient } from "instantsearch.js";
import { useSegment } from "src/hooks/useSegment";
import { useSession } from "next-auth/client";

export type ProductListProps = {
  slug: string;
  gender: string;
  pageContentType: PLPType;
  categoryPageId: string;
  indexName: string;
  searchClient: SearchClient;
  serverState?: InstantSearchServerState;
  excludeFilters?: string[];
  title?: string;
  navigationItems?: NavigationItems | null;
  hitsPerPage?: number;
};

function ProductList({
  indexName,
  searchClient,
  gender,
  slug,
  categoryPageId,
  pageContentType,
  serverState,
  excludeFilters,
  title,
  navigationItems,
  hitsPerPage = 72,
}: ProductListProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [session] = useSession();
  const { user: htUser } = useSegment();

  const filters = useMemo(() => {
    const filters = [
      `${GENDERS_FACET}:${localeFix({
        gender,
      })}`,
      `${IS_DELETED}:false`,
      `${BRAND_IS_PRIVATE_SALE}:false`,
    ];

    if (pageContentType === PLPType.NEW_IN) {
      filters.push(`${LABELS_TYPE_FACET}:new`);
    }

    if (pageContentType !== PLPType.NEW_IN) {
      filters.push(`${CATEGORY_PAGE_ID_FACET}:"${categoryPageId}"`);
    }

    if (pageContentType !== PLPType.BRAND_PLP) {
      filters.push(`${BRAND_DESIGNER_ONLY_FACET}:false`);
    }

    return filters.join(" AND ");
  }, [categoryPageId, gender, pageContentType]);

  const userToken = useMemo(
    () => session?.user.member_id || htUser?.anonymousId() || undefined,
    [session?.user, htUser]
  );

  return (
    <CategoryTreeProvider>
      <ListingContainer>
        <CategoryTreeWrapper>
          <AdditionalCategories navigationItems={navigationItems} />

          <CategoryTree
            gender={gender}
            categoryPageId={categoryPageId}
            pageContentType={pageContentType}
            brandName={title}
            gridRef={gridRef}
          />
        </CategoryTreeWrapper>

        <GridWrapper ref={gridRef}>
          {pageContentType !== PLPType.BRAND_PLP ? (
            <Promotion slug={slug} />
          ) : null}
          <InstantSearchSSRProvider key={null} initialResults={{}}>
            <InstantSearch
              searchClient={searchClient}
              future={{ preserveSharedStateOnUnmount: true }}
              indexName={indexName}
            >
              {[
                CATEGORIES_FACET,
                BRAND_FACET,
                SIZE_FACET,
                PRICE_FACET,
                COLOUR_FACET,
                PATTERN_FACET,
                STYLE_FACET,
                DISCOUNT_FACET,
              ].map((facet) => (
                <VirtualRefinementList key={facet} attribute={facet} />
              ))}
              <Configure
                hitsPerPage={72}
                filters={filters}
                disjunctiveFacets={[
                  CATEGORY_PAGE_ID_FACET,
                  CATEGORIES_FACET,
                  BRAND_FACET,
                  SIZE_FACET,
                  PRICE_FACET,
                  COLOUR_FACET,
                  PATTERN_FACET,
                  STYLE_FACET,
                  DISCOUNT_FACET,
                ]}
                maxValuesPerFacet={1000}
                getRankingInfo
                ruleContexts={[WEB_SHOP, contextKeys[pageContentType]]}
                analyticsTags={[WEB_SHOP, localeFix({ gender })]}
                userToken={userToken}
                clickAnalytics
              />
              <Grid
                title={title}
                indexName={indexName}
                gender={gender}
                pageContentType={pageContentType}
                slug={slug}
                categoryPageId={categoryPageId}
                excludeFilters={excludeFilters}
                hitsPerPage={hitsPerPage}
                serverState={serverState}
                gridRef={gridRef}
              />
            </InstantSearch>
          </InstantSearchSSRProvider>
        </GridWrapper>
      </ListingContainer>
    </CategoryTreeProvider>
  );
}

export { ProductList };
